import axios from "axios";
import { ActionCreator, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import { message, notification } from "antd";
import { MyDataAxios } from "../Contracts/Axios/MyDataAxios";
import { store } from "../Common/Store";
import * as UserActions from "../Actions/UserActions";

/// <reference path="../Typings/Jquery.fileDownload.d.ts" />
import * as DashboardMessageLogic from "../Logic/DashboardMessageLogic";
import {
    ICloudFileDatabasePage,
    ICloudFile,
    ICloudFolder,
    IDashboardMessage,
    IPushpinType,
    ITerritoryType,
    IDatabasePage,
    IMyData,
    IMyDataFilter,
    IMyDataImportResult,
    IPushpin,
    IServiceResult,
    IStore,
    ITerritory,
    IStoreType, ITargetMarketLocation,
    ITargetMarketDeveloperType,
} from "../Models/Models";
import * as types from "./Types";
import { ActiveApp } from "../Enums/Enums";
import * as actions from "./Actions";
import { IStoreUpdateAuditResult } from "../Models/IStoreUpdateAuditResult";
import { getStoreColumns } from "../Components/Apps/My Data/Logic/MyDataLogic";
import { IBadGeocode } from "../Models/IBadGeocode";
import {ScoutFeature} from "../Enums/ScoutFeature";

declare let scout;
declare let Common;

export const myDataLoaded = (loaded: boolean) => ({
    type: types.MY_DATA_LOADED,
    data: loaded,
});

export const updateCheckedColumns = (index: number) => ({
    type: types.UPDATE_MY_DATA_COLUMNS,
    data: index,
});

const loadMyDataTypesSuccess = (data: {}) => ({
    type: types.LOAD_MY_DATA_TYPES_SUCCESS,
    data: data,
});

const loadMyDataTypesFailure = (data: {}) => ({
    type: types.LOAD_MY_DATA_FAILURE,
    data: data,
});

const setBadGeocodes = (data: IBadGeocode[]) => ({
    type: types.MY_DATA_LOAD_STORE_SET_BAD_GEOCODES,
    data: data,
});

export const clearBadGeocodes: ActionCreator<ThunkAction<any, any, any, any>>
  = () => {
      return (dispatch: Dispatch<any>) => {
          dispatch(setBadGeocodes([]));
      };
  };

//This helper function is used to retry a fetchFn a number of times with a delay between each attempt
//It is used to give the server time to process (5000ms) the data before trying again
const fetchWithRetry = async (fetchFn: () => Promise<any>, retries = 2, delay = 1000, timeout = 5000) => {
    for (let i = 0; i < retries; i++) {
        try {
            const result = await Promise.race([
                fetchFn(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeout))
            ]);
            return result;
        } catch (error) {
            if (i < retries - 1) {
                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                throw error;
            }
        }
    }
};

export const loadMyDataTypes: ActionCreator<ThunkAction<any, any, any, any>>
  = () => {
      return (dispatch: Dispatch<any>) => {
          const myDataAjax = new MyDataAxios();
          const state = store.getState();
          const user = state.user.user;
          const promises = [];

          promises.push(myDataAjax.GetStoreTypes());
          promises.push(myDataAjax.GetPushpinTypes());
          promises.push(myDataAjax.GetTerritoryTypes());
          // Only call GetTargetMarkets if the user has that feature; otherwise, push a resolved promise with empty data.
          if (user && UserActions.hasFeature(user, ScoutFeature.TargetMarket)) {
              promises.push(myDataAjax.GetTargetMarkets());
          } else {
              promises.push(Promise.resolve({ data: [] }));
          }
          // promises.push(myDataAjax.GetAreasOfInterestPointTypes());
          // promises.push(myDataAjax.GetAreasOfInterestShapeTypes());
          // promises.push(myDataAjax.GetResultsPointTypes());
          // promises.push(myDataAjax.GetResultsShapeTypes());

          axios
              .all(promises)
          // .then(axios.spread((stores, pushpins, territories, aoiPoints, aoiShapes, resultsPoints, resultsShapes) => {
              .then(
                  axios.spread((stores, pushpins, territories, targetMarkets) => {
                      const data = {
                          locations: stores.data,
                          pushpins: pushpins.data,
                          territories: territories.data,
                          targetMarkets: targetMarkets.data,
                          // aoiPoints: aoiPoints.data,
                          // aoiShapes: aoiShapes.data,
                          // resultsPoints: resultsPoints.data,
                          // resultsShapes: resultsShapes.data
                      };
                      dispatch(loadMyDataTypesSuccess(data));
                  })
              )
              .catch((error: any) => {
                  dispatch(loadMyDataTypesFailure(error));
              });
      };
  };

export const updateSortOrder = (column) => {
    const columnToSort = store.getState().myData.columnToSort;
    let sortOrder = null;

    !columnToSort.sortDirection
        ? (sortOrder = "DESC")
        : columnToSort.sortDirection === "DESC"
            ? (sortOrder = "ASC")
            : columnToSort.sortDirection === "ASC"
                ? (sortOrder = null)
                : (sortOrder = null);

    const toSort: { sortColumn: string; sortDirection: string | boolean } = {
        sortColumn: column.dataIndex,
        sortDirection: sortOrder,
    };

    store.dispatch({ type: types.UPDATE_SORT_DIRECTION, data: toSort });
};

export const resetSortData = () => ({
    type: types.RESET_SORT_DIRECTION,
});

export const typesChanged = () => ({
    type: types.MY_DATA_TYPES_CHANGED,
});

const updateCloudFileSortOrder = (column) => {
    const columnToSort = store.getState().myData.columnToSort;
    let sortOrder = null;

    //clicked on same column as previously sorted column so update sort direction accordingly
    if (columnToSort.sortColumn === column.dataIndex) {
        !columnToSort.sortDirection
            ? (sortOrder = "DESC")
            : columnToSort.sortDirection === "DESC"
                ? (sortOrder = "ASC")
                : columnToSort.sortDirection === "ASC"
                    ? (sortOrder = null)
                    : (sortOrder = null);
    } else {
        sortOrder = "DESC";
    }

    const toSort: { sortColumn: string; sortDirection: string | boolean } = {
        sortColumn: sortOrder === null ? null : column.dataIndex,
        sortDirection: sortOrder,
    };

    store.dispatch({ type: types.UPDATE_SORT_DIRECTION, data: toSort });
};

const loadPushpinTypesSuccess = (data: IPushpinType) => ({
    type: types.MY_DATA_LOAD_PUSHPIN_TYPES_SUCCESS,
    data: data,
});

const loadPushpinTypesFailure = (error) => ({
    type: types.MY_DATA_LOAD_PUSHPIN_TYPES_FAILURE,
    data: error,
});

const loadStoreTypesSuccess = (data: IStoreType) => ({
    type: types.MY_DATA_LOAD_STORE_TYPES_SUCCESS,
    data: data,
});

const loadStoreTypesFailure = (error) => ({
    type: types.MY_DATA_LOAD_STORE_TYPES_FAILURE,
    data: error,
});

const loadTerritoryTypesSuccess = (data: ITerritoryType) => ({
    type: types.MY_DATA_LOAD_TERRITORY_TYPES_SUCCESS,
    data: data,
});

const loadTerritoryTypesFailure = (error) => ({
    type: types.MY_DATA_LOAD_TERRITORY_TYPES_FAILURE,
    data: error,
});

const setActiveType = (key: string) => ({
    type: types.MY_DATA_SET_ACTIVE_TYPE,
    data: key,
});

const setBreadcrumbs = (data: Array<string>) => ({
    type: types.MY_DATA_SET_BREADCRUMBS,
    data: data,
});

export const updateBreadcrumbs: ActionCreator<ThunkAction<any, any, any, any>>
  = (area: string, name: string) => {
      return (dispatch: Dispatch<any>) => {
          if (area.includes("Areas of Interest") || area.includes("Results")) {
              const newArea = area.split("/");
              newArea.push(name);

              dispatch(setBreadcrumbs(newArea));
          } else {
              dispatch(setBreadcrumbs([area, name]));
          }
      };
  };

const importSuccessNotification = (
    dispatch: Dispatch<any>,
    type: string,
    importedType
): void => {
    if (store.getState().loadScout.scoutIsLoaded) {
        const message: IDashboardMessage = {
            targetElement: "dashboardMessageReceiverMyData",
            message: {
                text: "REFRESH_DATA",
                data: null,
            },
        };

        DashboardMessageLogic.sendDashBoardMessage(message);
    }

    importedType.id = importedType.id ?? importedType.typeID ?? importedType.typeId;
    importedType.label = importedType.label ?? importedType.name ?? importedType.typeName;
    importedType.fullPath = importedType.fullPath ?? importedType.iconName ?? importedType.imagePath;

    const typeData = {
        area: type,
        filters: [],
        type: {
            id: importedType.id,
            // I hate that I have to duplicate this, but some code paths need id, others need typeId.
            typeId: importedType.id,
            label: importedType.label,
            fullPath: importedType.fullPath,
        },
        rowStart: 0,
        rowEnd: 25,
        sortColumn: null,
        sortDirection: null,
    };

    message.destroy();
    notification["success"]({
        message: "Import Completed",
        description: "Your data has finished processing. Click here to view.",
        onClick: () => {
            dispatch(setLoading(true));
            notification.destroy();

            if (store.getState().loadScout.activeApp !== ActiveApp.MY_DATA) {
                dispatch(actions.setActiveApp(ActiveApp.MY_DATA));
                $("#experimental-scout").hide();
                //this.props.closeLeftNav(ActiveApp.MY_DATA);
                dispatch(actions.setScoutIsShowing(false));

                window.location.hash = "/data/";
            }

            //wait for My Data to load before switching to imported type
            const loadImportedType = setInterval(() => {
                if (store.getState().myData.myDataLoaded) {
                    if (type === "Pushpins" || type === "Territories" || type === "Locations") {
                        const myDataState = store.getState().myData;
                        const typeArray =  myDataState.types[type.toLowerCase()];

                        for (const i in typeArray) {
                            if (typeArray[i].typeID === importedType.typeID) {
                                let key: string;

                                switch (type) {
                                    case "Pushpins":
                                        key = "3" + i;
                                        break;
                                    case "Territories":
                                        key = "2" + i;
                                        break;
                                    case "Locations":
                                        key = "4" + i;
                                        break;
                                    default:
                                        key = i;
                                        break;
                                }
                                dispatch(setActiveType(key));
                                break;
                            }
                        }
                    } else if (type === "Target Markets") {
                        const myDataState = store.getState().myData;
                        const developers =  myDataState.types["targetMarkets"];

                        let foundTheType = false;
                        for (const developer of developers) {
                            for (const type of developer.types) {
                                if (type.typeId === importedType.typeId) {
                                    const key = `targetMarkets-types-${type.typeId.toString()}`;
                                    dispatch(setActiveType(key));
                                    foundTheType = true;
                                    break;
                                }
                            }

                            // Types are nested within developers, and the same type can be present in multiple developers.
                            // So this keeps us from continuing through the rest of the developers.
                            if (foundTheType) {
                                break;
                            }
                        }
                    }

                    const name: string = type === "Pushpins"
                        ? importedType.label
                        : importedType.name;

                    if (type === "Results/Points" || type === "Results/Geographies") {
                        const parentPath = importedType.parentDirectoryFullPath.split("/");
                        parentPath.splice(0, 4, type);
                        parentPath.pop();
                        dispatch(updateBreadcrumbs(parentPath.join("/"), name));
                    } else if (type === "Areas of Interest/Points" || type === "Areas of Interest/Geographies") {
                        const parentPath = importedType.parentDirectoryFullPath.split("/");
                        parentPath.splice(0, 5, type);
                        parentPath.pop();
                        dispatch(updateBreadcrumbs(parentPath.join("/"), name));
                    } else {
                        dispatch(updateBreadcrumbs(type, name));
                    }

                    dispatch(loadTypeData(typeData));
                    clearInterval(loadImportedType);
                }
            }, 500);
        },
        duration: 0,
        style: { cursor: "pointer" },
    });
};

const importFailureNotification = (errorMessage: string): void => {
    message.destroy();
    notification["error"]({
        message: "Import Has Errors",
        description:
      errorMessage && errorMessage.trim() !== ""
          ? errorMessage
          : "Your import has errors. Please try again.",
        onClick: () => {
            notification.destroy();
        },
        duration: 0,
        style: { cursor: "pointer" },
    });
};

export const submitImport: ActionCreator<ThunkAction<any, any, any, any>> = (
    data,
    url
) => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();
        const promise = myDataAjax.SubmitImport(data, url);

        const dataType = data.dataType;

        if (dataType === "pushpin") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        //dispatch(submitImportGeocodeSuccess(response.data));

                        importSuccessNotification(
                            dispatch,
                            "Pushpins",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetPushpinTypes();

                        promises
                            .then((response: IServiceResult<IPushpinType>) => {
                                dispatch(loadPushpinTypesSuccess(response.data));

                                //Refresh types in SCOUT
                                scout.context.getCurrentUser();
                            })
                            .catch((error: any) => {
                                dispatch(loadPushpinTypesFailure(error));
                            });
                    } else {
                        //dispatch(submitImportFailure(response.data));
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    //dispatch(submitImportFailure(error));
                    importFailureNotification(error.message);
                });
        } else if (dataType === "store") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        importSuccessNotification(
                            dispatch,
                            "Locations",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetStoreTypes();

                        const badGeocodes = response.data.needsGeocodeAttention;
                        dispatch(setBadGeocodes(badGeocodes));

                        promises
                            .then((response: IServiceResult<IStoreType>) => {
                                dispatch(loadStoreTypesSuccess(response.data));

                                //Refresh types in SCOUT
                                scout.context.getCurrentUser();
                            })
                            .catch((error: any) => {
                                dispatch(loadStoreTypesFailure(error));
                            });
                    } else {
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    importFailureNotification(error.message);
                });
        } else if (dataType === "territory") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        //dispatch(submitImportSuccess(response.data));
                        importSuccessNotification(
                            dispatch,
                            "Territories",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetTerritoryTypes();

                        promises
                            .then((response: IServiceResult<ITerritoryType>) => {
                                dispatch(loadTerritoryTypesSuccess(response.data));

                                //Refresh types in SCOUT
                                scout.context.getCurrentUser();
                            })
                            .catch((error: any) => {
                                dispatch(loadTerritoryTypesFailure(error));
                            });
                    } else {
                        //dispatch(submitImportFailure(response.data));
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    //dispatch(submitImportFailure(error));
                    importFailureNotification(error.message);
                });
        } else if (dataType === "aoiPoint") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        //dispatch(submitImportSuccess(response.data));
                        importSuccessNotification(
                            dispatch,
                            "Areas of Interest/Points",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIPointsSuccess(response.data));

                                //Refresh type in SCOUT
                                scout.context.updatePointAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIPointsFailure(error));
                            });
                    } else {
                        //dispatch(submitImportFailure(response.data));
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    //dispatch(submitImportFailure(error));
                    importFailureNotification(error.message);
                });
        } else if (dataType === "resultsPoint") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        //dispatch(submitImportSuccess(response.data));
                        importSuccessNotification(
                            dispatch,
                            "Results/Points",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsPointsSuccess(response.data));

                                //Refresh type in SCOUT
                                scout.context.updatePointResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsPointsFailure(error));
                            });
                    } else {
                        //dispatch(submitImportFailure(response.data));
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    //dispatch(submitImportFailure(error));
                    importFailureNotification(error.message);
                });
        } else if (dataType === "aoiShape") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        //dispatch(submitImportSuccess(response.data));
                        importSuccessNotification(
                            dispatch,
                            "Areas of Interest/Geographies",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIShapesSuccess(response.data));

                                //Refresh type in SCOUT
                                scout.context.updateShapeAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIShapesFailure(error));
                            });
                    } else {
                        //dispatch(submitImportFailure(response.data));
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    //dispatch(submitImportFailure(error));
                    importFailureNotification(error.message);
                });
        } else if (dataType === "resultsShape") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        //dispatch(submitImportSuccess(response.data));
                        importSuccessNotification(
                            dispatch,
                            "Results/Geographies",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsShapesSuccess(response.data));

                                //Refresh type
                                scout.context.updateShapeResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsShapesFailure(error));
                            });
                    } else {
                        //dispatch(submitImportFailure(response.data));
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    //dispatch(submitImportFailure(error));
                    importFailureNotification(error.message);
                });
        } else if (dataType === "targetMarket") {
            promise
                .then((response: IServiceResult<IMyDataImportResult>) => {
                    if (response.result) {
                        importSuccessNotification(
                            dispatch,
                            "Target Markets",
                            response.data.importedType
                        );

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetTargetMarkets();

                        const badGeocodes = response.data.needsGeocodeAttention;
                        dispatch(setBadGeocodes(badGeocodes));

                        promises
                            .then((response: IServiceResult<ITargetMarketDeveloperType>) => {
                                dispatch(loadTargetMarketLocationsSuccess(response.data));

                                //Refresh types in SCOUT
                                scout.context.getCurrentUser();
                            })
                            .catch((error: any) => {
                                dispatch(loadStoreTypesFailure(error));
                            });
                    } else {
                        importFailureNotification(response.messages.join(", "));
                    }
                })
                .catch((error: Error) => {
                    importFailureNotification(error.message);
                });
        }
    };
};

export const submitAudit: ActionCreator<ThunkAction<any, any, any, any>> = (
    data,
    url,
    callback: (response: IServiceResult<IStoreUpdateAuditResult>) => void
) => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();
        const promise = myDataAjax.SubmitAudit(data, url);

        promise
            .then((response: IServiceResult<IStoreUpdateAuditResult>) => {
                callback(response);
                dispatch(loadStoreFileAuditSuccess(response.data));
            })
            .catch((error: Error) => {
                importFailureNotification(error.message);
                dispatch(loadStoreFileAuditFail(error.message));
            });
    };
};

const loadMyDataCloudTypesSuccess = (data: {}) => ({
    type: types.LOAD_MY_DATA_CLOUD_TYPES_SUCCESS,
    data: data,
});

const loadMyDataCloudTypesFailure = (data: {}) => ({
    type: types.LOAD_MY_DATA_CLOUD_TYPES_FAILURE,
    data: data,
});

export const resetAddCloudFolderMessage = () => ({
    type: types.MY_DATA_RESET_ADD_CLOUD_FOLDER_MESSAGE,
});

const addCloudFolderSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_ADD_CLOUD_FOLDER_SUCCESS,
    data: data,
});

const addCloudFolderFailure = (error) => ({
    type: types.MY_DATA_ADD_CLOUD_FOLDER_FAILURE,
    data: error,
});

const loadResultsPointsSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_LOAD_RESULTS_POINTS_TYPES_SUCCESS,
    data: data,
});

const loadResultsPointsFailure = (error) => ({
    type: types.MY_DATA_LOAD_RESULTS_POINTS_TYPES_FAILURE,
    data: error,
});

const loadResultsShapesSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_LOAD_RESULTS_SHAPES_TYPES_SUCCESS,
    data: data,
});

const loadResultsShapesFailure = (error) => ({
    type: types.MY_DATA_LOAD_RESULTS_SHAPES_TYPES_FAILURE,
    data: error,
});

const loadAOIPointsSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_LOAD_AOI_POINTS_TYPES_SUCCESS,
    data: data,
});

const loadAOIPointsFailure = (error) => ({
    type: types.MY_DATA_LOAD_AOI_POINTS_TYPES_FAILURE,
    data: error,
});

const loadAOIShapesSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_LOAD_AOI_SHAPES_TYPES_SUCCESS,
    data: data,
});

const loadAOIShapesFailure = (error) => ({
    type: types.MY_DATA_LOAD_AOI_SHAPES_TYPES_FAILURE,
    data: error,
});

const loadTargetMarketLocationsSuccess = (data: any) => ({
    type: types.MY_DATA_LOAD_TARGET_MARKET_LOCATIONS_SUCCESS,
    data: data,
});

const loadTargetMarketLocationsFailure = (error) => ({
    type: types.MY_DATA_LOAD_TARGET_MARKET_LOCATIONS_FAILURE,
    data: error,
});

export const buildCurrentCloudTypeDict = (dataType: string) => ({
    type: types.MY_DATA_BUILD_CURRENT_CLOUD_TYPE_DICT,
    data: dataType,
});

export const loadStoreFileAuditSuccess = (
    dataType: IStoreUpdateAuditResult
) => ({
    type: types.MY_DATA_LOAD_STORE_FILE_AUDIT_SUCCESS,
    data: dataType,
});

export const loadStoreFileAuditFail = (error: string) => ({
    type: types.MY_DATA_LOAD_STORE_FILE_AUDIT_FAIL,
    data: error,
});

export const addCloudFolder: ActionCreator<ThunkAction<any, any, any, any>> = (
    data
) => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();
        const promise = myDataAjax.AddCloudFolder(data);

        const dataType = data.dataType;
        const fullPath = data.parentFullPath;

        if (dataType === "resultsPoint") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(addCloudFolderSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsPointsSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updatePointResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsPointsFailure(error));
                            });
                    } else {
                        dispatch(addCloudFolderFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(addCloudFolderFailure(error));
                });
        } else if (dataType === "resultsShape") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(addCloudFolderSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsShapesSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updateShapeResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsShapesFailure(error));
                            });
                    } else {
                        dispatch(addCloudFolderFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(addCloudFolderFailure(error));
                });
        } else if (dataType === "aoiPoint") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(addCloudFolderSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIPointsSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updatePointAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIPointsFailure(error));
                            });
                    } else {
                        dispatch(addCloudFolderFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(addCloudFolderFailure(error));
                });
        } else if (dataType === "aoiShape") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(addCloudFolderSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIShapesSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updateShapeAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIShapesFailure(error));
                            });
                    } else {
                        dispatch(addCloudFolderFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(addCloudFolderFailure(error));
                });
        }
    };
};

export const setCurrentCloudFolder = (folderFullPath: string) => ({
    type: types.MY_DATA_SET_CURRENT_CLOUD_FOLDER,
    data: folderFullPath,
});

const deleteCloudFolderSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_DELETE_CLOUD_FOLDER_SUCCESS,
    data: data,
});

const deleteCloudFolderFailure = (error) => ({
    type: types.MY_DATA_DELETE_CLOUD_FOLDER_FAILURE,
    data: error,
});

export const deleteCloudFolder: ActionCreator<ThunkAction<any, any, any, any>>
  = (data) => {
      return (dispatch: Dispatch<any>) => {
          const myDataAjax = new MyDataAxios();
          const promise = myDataAjax.DeleteCloudFolder(data);

          const dataType = data.dataType;
          const fullPath = data.parent;

          if (dataType === "resultsPoint") {
              promise
                  .then((response: IServiceResult<ICloudFolder>) => {
                      if (response.result) {
                          dispatch(deleteCloudFolderSuccess(response.data));

                          const myDataAjax = new MyDataAxios();
                          const promises = myDataAjax.GetResultsPointTypes();

                          promises
                              .then((response: IServiceResult<ICloudFolder>) => {
                                  dispatch(loadResultsPointsSuccess(response.data));
                                  dispatch(setCurrentCloudFolder(fullPath));

                                  //Refresh type in SCOUT
                                  scout.context.updatePointResults();
                              })
                              .catch((error: any) => {
                                  dispatch(loadResultsPointsFailure(error));
                              });
                      } else {
                          dispatch(deleteCloudFolderFailure(response.messages[0]));
                      }
                  })
                  .catch((error: any) => {
                      dispatch(deleteCloudFolderFailure(error));
                  });
          } else if (dataType === "resultsShape") {
              promise
                  .then((response: IServiceResult<ICloudFolder>) => {
                      if (response.result) {
                          dispatch(deleteCloudFolderSuccess(response.data));

                          const myDataAjax = new MyDataAxios();
                          const promises = myDataAjax.GetResultsShapeTypes();

                          promises
                              .then((response: IServiceResult<ICloudFolder>) => {
                                  dispatch(loadResultsShapesSuccess(response.data));
                                  dispatch(setCurrentCloudFolder(fullPath));

                                  //Refresh type in SCOUT
                                  scout.context.updateShapeResults();
                              })
                              .catch((error: any) => {
                                  dispatch(loadResultsShapesFailure(error));
                              });
                      } else {
                          dispatch(deleteCloudFolderFailure(response.messages[0]));
                      }
                  })
                  .catch((error: any) => {
                      dispatch(deleteCloudFolderFailure(error));
                  });
          } else if (dataType === "aoiPoint") {
              promise
                  .then((response: IServiceResult<ICloudFolder>) => {
                      if (response.result) {
                          dispatch(deleteCloudFolderSuccess(response.data));

                          const myDataAjax = new MyDataAxios();
                          const promises = myDataAjax.GetAreasOfInterestPointTypes();

                          promises
                              .then((response: IServiceResult<ICloudFolder>) => {
                                  dispatch(loadAOIPointsSuccess(response.data));
                                  dispatch(setCurrentCloudFolder(fullPath));

                                  //Refresh type in SCOUT
                                  scout.context.updatePointAreasOfInterestUserFolder();
                              })
                              .catch((error: any) => {
                                  dispatch(loadAOIPointsFailure(error));
                              });
                      } else {
                          dispatch(deleteCloudFolderFailure(response.messages[0]));
                      }
                  })
                  .catch((error: any) => {
                      dispatch(deleteCloudFolderFailure(error));
                  });
          } else if (dataType === "aoiShape") {
              promise
                  .then((response: IServiceResult<ICloudFolder>) => {
                      if (response.result) {
                          dispatch(deleteCloudFolderSuccess(response.data));

                          const myDataAjax = new MyDataAxios();
                          const promises = myDataAjax.GetAreasOfInterestShapeTypes();

                          promises
                              .then((response: IServiceResult<ICloudFolder>) => {
                                  dispatch(loadAOIShapesSuccess(response.data));
                                  dispatch(setCurrentCloudFolder(fullPath));

                                  //Refresh type in SCOUT
                                  scout.context.updateShapeAreasOfInterestUserFolder();
                              })
                              .catch((error: any) => {
                                  dispatch(loadAOIShapesFailure(error));
                              });
                      } else {
                          dispatch(deleteCloudFolderFailure(response.messages[0]));
                      }
                  })
                  .catch((error: any) => {
                      dispatch(deleteCloudFolderFailure(error));
                  });
          }
      };
  };

export const updateCloudFileLoading = () => ({
    type: types.MY_DATA_UPDATE_CLOUD_FILE_LOADING,
});

export const resetUpdateCloudFileMessage = () => ({
    type: types.MY_DATA_RESET_UPDATE_CLOUD_FILE_MESSAGE,
});

const updateCloudFileSuccess = (data: ICloudFile) => ({
    type: types.MY_DATA_UPDATE_CLOUD_FILE_SUCCESS,
    data: data,
});

const updateCloudFileFailure = (error) => ({
    type: types.MY_DATA_UPDATE_CLOUD_FILE_FAILURE,
    data: error,
});

export const updateCloudFile: ActionCreator<ThunkAction<any, any, any, any>> = (
    data
) => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();
        const promise = myDataAjax.UpdateCloudFile(data);

        const dataType = data.dataType;
        const fullPath = data.parent;

        if (dataType === "resultsPoint") {
            promise
                .then((response: IServiceResult<ICloudFile>) => {
                    if (response.result) {
                        dispatch(updateCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsPointsSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updatePointResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsPointsFailure(error));
                            });
                    } else {
                        dispatch(updateCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(updateCloudFileFailure(error));
                });
        } else if (dataType === "resultsShape") {
            promise
                .then((response: IServiceResult<ICloudFile>) => {
                    if (response.result) {
                        dispatch(updateCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsShapesSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.getShapeResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsShapesFailure(error));
                            });
                    } else {
                        dispatch(updateCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(updateCloudFileFailure(error));
                });
        } else if (dataType === "aoiPoint") {
            promise
                .then((response: IServiceResult<ICloudFile>) => {
                    if (response.result) {
                        dispatch(updateCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIPointsSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updatePointAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIPointsFailure(error));
                            });
                    } else {
                        dispatch(updateCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(updateCloudFileFailure(error));
                });
        } else if (dataType === "aoiShape") {
            promise
                .then((response: IServiceResult<ICloudFile>) => {
                    if (response.result) {
                        dispatch(updateCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIShapesSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updateShapeAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIShapesFailure(error));
                            });
                    } else {
                        dispatch(updateCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(updateCloudFileFailure(error));
                });
        }
    };
};

const deleteResultsPointsCloudFileSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_RESULTS_POINTS_DELETE_CLOUD_FILE_SUCCESS,
    data: data,
});

const deleteResultsShapesCloudFileSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_RESULTS_SHAPES_DELETE_CLOUD_FILE_SUCCESS,
    data: data,
});

const deleteAOIPointsCloudFileSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_AOI_POINTS_DELETE_CLOUD_FILE_SUCCESS,
    data: data,
});

const deleteAOIShapesCloudFileSuccess = (data: ICloudFolder) => ({
    type: types.MY_DATA_AOI_SHAPES_DELETE_CLOUD_FILE_SUCCESS,
    data: data,
});

const deleteCloudFileFailure = (error) => ({
    type: types.MY_DATA_DELETE_CLOUD_FILE_FAILURE,
    data: error,
});

export const deleteCloudFile: ActionCreator<ThunkAction<any, any, any, any>> = (
    data
) => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();
        const promise = myDataAjax.DeleteCloudFile(data);

        const dataType = data.dataType;
        const fullPath = data.parent;

        if (dataType === "resultsPoint") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(deleteResultsPointsCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsPointsSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updatePointResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsPointsFailure(error));
                            });
                    } else {
                        dispatch(deleteCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(deleteCloudFileFailure(error));
                });
        } else if (dataType === "resultsShape") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(deleteResultsShapesCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetResultsShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadResultsShapesSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.getShapeResults();
                            })
                            .catch((error: any) => {
                                dispatch(loadResultsShapesFailure(error));
                            });
                    } else {
                        dispatch(deleteCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(deleteCloudFileFailure(error));
                });
        } else if (dataType === "aoiPoint") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(deleteAOIPointsCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestPointTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIPointsSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updatePointAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIPointsFailure(error));
                            });
                    } else {
                        dispatch(deleteCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(deleteCloudFileFailure(error));
                });
        } else if (dataType === "aoiShape") {
            promise
                .then((response: IServiceResult<ICloudFolder>) => {
                    if (response.result) {
                        dispatch(deleteAOIShapesCloudFileSuccess(response.data));

                        const myDataAjax = new MyDataAxios();
                        const promises = myDataAjax.GetAreasOfInterestShapeTypes();

                        promises
                            .then((response: IServiceResult<ICloudFolder>) => {
                                dispatch(loadAOIShapesSuccess(response.data));
                                dispatch(setCurrentCloudFolder(fullPath));

                                //Refresh type in SCOUT
                                scout.context.updateShapeAreasOfInterestUserFolder();
                            })
                            .catch((error: any) => {
                                dispatch(loadAOIShapesFailure(error));
                            });
                    } else {
                        dispatch(deleteCloudFileFailure(response.messages[0]));
                    }
                })
                .catch((error: any) => {
                    dispatch(deleteCloudFileFailure(error));
                });
        }
    };
};

const loadMyDataDataSuccess = (data: IMyData) => ({
    type: types.LOAD_MY_DATA_DATA_SUCCESS,
    data: data,
});

const loadMyDataDataFailure = (data: IMyData) => ({
    type: types.LOAD_MY_DATA_FAILURE,
    data: data,
});

const loadMyDataOtherSuccess = (data: IMyData) => ({
    type: types.LOAD_MY_DATA_OTHER_SUCCESS,
    data: data,
});

export const setCurrentPage = (currentPage: number) => ({
    type: types.MY_DATA_SET_CURRENT_PAGE,
    data: currentPage,
});

export const setPageSize = (currentPageSize: number) => ({
    type: types.MY_DATA_SET_PAGE_SIZE,
    data: currentPageSize,
});

export const loadTypeData: ActionCreator<ThunkAction<any, any, any, any>> = (
    data
) => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();
        let promise = null;

        switch (data.area) {
            case "Locations":
                promise = fetchWithRetry(() => myDataAjax.GetStoreData(data));

                promise
                    .then((response: IServiceResult<IDatabasePage<IStore>>) => {
                        response.data.data.map((mappedObject, index) => {
                            mappedObject.key = index;
                            mappedObject.typeID = data.type.id;
                        });

                        const activeData = [];
                        response.data.data.map((mappedObject) => {
                            activeData.push(mappedObject);
                        });

                        const companyType = store.getState().user.user.company.companyType;

                        data = {
                            dataSize: response.data.maxRows,
                            activeArea: data.area,
                            activeType: data.type,
                            activeData: activeData,
                            activeColumns: getStoreColumns(activeData, companyType),
                        };
                        dispatch(loadMyDataDataSuccess(data));
                    })
                    .catch((error: any) => {
                        //This custom error message is for when the request times out for a large data set from the initial load
                        if (error.message === "Timeout") {
                            error.message = "Please select an item in the left nav.";
                        }
                        dispatch(loadMyDataDataFailure(error));
                    });

                break;
            case "Pushpins":
                promise = fetchWithRetry(() => myDataAjax.GetPushpinData(data));

                promise
                    .then((response: IServiceResult<IDatabasePage<IPushpin>>) => {
                        response.data.data.map((mappedObject, index) => {
                            mappedObject.key = index;
                            mappedObject.typeID = data.type.typeID;
                        });

                        const activeData = [];
                        response.data.data.map((mappedObject) => {
                            activeData.push(mappedObject);
                        });

                        data = {
                            dataSize: response.data.maxRows,
                            activeArea: data.area,
                            activeType: data.type,
                            activeData: activeData,
                            //activeColumns: pushpinColumns
                        };
                        dispatch(loadMyDataDataSuccess(data));
                    })
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });

                break;
            case "Territories":
                promise = fetchWithRetry(() => myDataAjax.GetTerritoryData(data));

                promise
                    .then((response: IServiceResult<IDatabasePage<ITerritory>>) => {
                        response.data.data.map((mappedObject, index) => {
                            mappedObject.key = index;
                            mappedObject.typeID = data.type.typeID;
                        });

                        const activeData = [];
                        response.data.data.map((mappedObject) => {
                            activeData.push(mappedObject);
                        });

                        data = {
                            dataSize: response.data.maxRows,
                            activeArea: data.area,
                            activeType: data.type,
                            activeData: activeData,
                            //activeColumns: territoryColumns
                        };
                        dispatch(loadMyDataDataSuccess(data));
                    })
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });
                break;
            case "Results/Points":
                promise = fetchWithRetry(() => myDataAjax.GetResultsPointData(data));

                promise
                    .then(
                        (
                            response: IServiceResult<
                ICloudFileDatabasePage<{ [key: string]: string }>
              >
                        ) => {
                            successOtherLoad(data, response, dispatch);
                        }
                    )
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });

                break;
            case "Results/Geographies":
                promise = fetchWithRetry(() => myDataAjax.GetResultsShapeData(data));

                promise
                    .then(
                        (
                            response: IServiceResult<
                ICloudFileDatabasePage<{ [key: string]: string }>
              >
                        ) => {
                            successOtherLoad(data, response, dispatch);
                        }
                    )
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });

                break;
            case "Areas of Interest/Points":
                promise = fetchWithRetry(() => myDataAjax.GetAreasOfInterestPointData(data));

                promise
                    .then(
                        (
                            response: IServiceResult<
                ICloudFileDatabasePage<{ [key: string]: string }>
              >
                        ) => {
                            successOtherLoad(data, response, dispatch);
                        }
                    )
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });

                break;
            case "Areas of Interest/Geographies":
                promise = fetchWithRetry(() => myDataAjax.GetAreasOfInterestShapeData(data));

                promise
                    .then(
                        (
                            response: IServiceResult<
                ICloudFileDatabasePage<{ [key: string]: string }>
              >
                        ) => {
                            successOtherLoad(data, response, dispatch);
                        }
                    )
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });

                break;
            case "Target Markets":
                promise = fetchWithRetry(() => myDataAjax.GetTargetMarketData(data), 2, 1000, 15000);
                promise
                    .then((response: IServiceResult<IDatabasePage<ITargetMarketLocation>>) => {
                        response.data.data.map((mappedObject, index) => {
                            mappedObject.key = index;
                        });

                        const activeData = [];
                        response.data.data.map((mappedObject) => {
                            activeData.push(mappedObject);
                        });

                        data = {
                            dataSize: response.data.maxRows,
                            activeArea: data.area,
                            activeType: data.type,
                            activeData: activeData,
                        };
                        dispatch(loadMyDataDataSuccess(data));
                    })
                    .catch((error: any) => {
                        dispatch(loadMyDataDataFailure(error));
                    });
                break;
        }
    };
};

const successOtherLoad = (
    data,
    response: IServiceResult<ICloudFileDatabasePage<{ [key: string]: string }>>,
    dispatch: Dispatch
) => {
    // Adjust data keys to match column dataIndex values
    response.data.pagedData.data = response.data.pagedData.data.map((mappedObject, index) => {
        mappedObject.key = index;
        mappedObject.fullPath = data.type.fullPath;
        // Convert keys to match dataIndex values in columns
        const adjustedObject = {};
        Object.keys(mappedObject).forEach((key) => {
            adjustedObject[key.toLowerCase()] = mappedObject[key];
        });
        return adjustedObject;
    });

    const activeData = [];
    response.data.pagedData.data.map((mappedObject) => {
        activeData.push(mappedObject);
    });

    // Add header cell click event for sort and lower case dataIndex so data matches up with column
    response.data.columns.map((mappedObject) => {
        mappedObject.onHeaderCell = _headerCell;
    });

    // Filter out duplicate columns
    const columnObj = {};
    const filteredColumns = response.data.columns.filter((item) => {
        const dataIndex = item.dataIndex;
        if (columnObj[dataIndex]) {
            return false;
        } else {
            columnObj[dataIndex] = item;
            return true;
        }
    });

    data = {
        dataSize: response.data.pagedData.maxRows,
        activeArea: data.area,
        activeType: data.type,
        activeData: activeData,
        activeColumns: filteredColumns,
    };

    dispatch(loadMyDataOtherSuccess(data));
};


const _headerCell = (column) => {
    return {
        onClick: () => {
            updateCloudFileSortOrder(column);
        },
    };
};

export const removeAllFilters = () => ({
    type: types.MY_DATA_REMOVE_ALL_FILTERS,
});

export const removeFilter = (filter: IMyDataFilter) => ({
    type: types.MY_DATA_REMOVE_FILTER,
    data: filter,
});

export const updateFilters = (filter: IMyDataFilter) => ({
    type: types.UPDATE_MY_DATA_FILTERS,
    data: filter,
});

export const updateCollaborateTerritories = (bool: boolean) => ({
    type: types.UPDATE_MY_DATA_TERRITORY_COLLABORATE,
    data: bool,
});

export const updateCollaboratePushpins = (bool: boolean) => ({
    type: types.UPDATE_MY_DATA_PUSHPIN_COLLABORATE,
    data: bool,
});

export const setLoading = (bool: boolean) => ({
    type: types.SET_MY_DATA_LOADING,
    data: bool,
});

export const setMaxFirstLogicRecords = (records: number) => ({
    type: types.MY_DATA_SET_MAX_FIRSTLOGIC_RECORDS,
    data: records,
});

export const setMaxGoogleRecords = (records: number) => ({
    type: types.MY_DATA_SET_MAX_GOOGLE_RECORDS,
    data: records,
});

export const setUseNewGeocodingService = (useIt: boolean) => ({
    type: types.MY_DATA_SET_USE_NEW_GEOCODING_SERVICE,
    data: useIt,
});

export const loadConstants: ActionCreator<ThunkAction<any, any, any, any>> = () => {
    return (dispatch: Dispatch<any>) => {
        const myDataAjax = new MyDataAxios();

        myDataAjax.GetMyDataConstants()
            .then((response) => {
                const constants = response.data;
                dispatch(setMaxFirstLogicRecords(constants.maxFirstLogicRecords));
                dispatch(setMaxGoogleRecords(constants.maxGoogleRecords));
                dispatch(setUseNewGeocodingService(constants.useNewGeocodingService));
            })
            .catch((error: any) => {
                // Optionally dispatch a failure action or log
                console.error("Failed to load geocoder limits", error);
            });
    };
};


export const exportAll: ActionCreator<ThunkAction<any, any, any, any>> = (
    data
) => {
    return (dispatch: Dispatch<any>) => {
        let baseUrl = "";

        const args = {
            dataType: data.dataType,
            fileType: data.fileType,
            sortColumn: data.sortColumn,
            sortDirection: data.sortDirection,
            filters: null,
            typeId: null,
            fullPath: null,
            collaborate: data.collaborate,
            rowIds: null,
            userId: null,
            developerId: null, // For Target Market, we need to send developerId, not userId.
        };

        switch (data.dataType) {
            case "Locations":
                baseUrl = "/DataForge/Stores/Export";
                args.typeId = data.type.id;
                break;
            case "Pushpins":
                baseUrl = "/DataForge/Pushpins/Export";
                args.typeId = data.type.typeID;
                args.collaborate = !!data.collaborate;
                break;
            case "Territories":
                baseUrl = "/DataForge/Territories/Export";
                args.typeId = data.type.typeID;
                args.collaborate = !!data.collaborate;
                break;
            case "Areas of Interest/Points":
                baseUrl = "/DataForge/AreasOfInterest/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Areas of Interest/Geographies":
                baseUrl = "/DataForge/AreasOfInterest/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Results/Points":
                baseUrl = "/DataForge/Results/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Results/Geographies":
                baseUrl = "/DataForge/Results/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Target Markets":
                baseUrl = "/DataForge/TargetMarket/Export";
                args.typeId = data.type.typeId;
                args.developerId = data.type.developerId;

                // If no sort has been set, seed with default values.
                if (!args.sortColumn && !args.sortDirection) {
                    args.sortColumn = "locationName";
                    args.sortDirection = "ASC";
                }
                break;
            default:
                return;
        }

        const success = () => {
            console.log("Successfully Exported All Rows");
        };

        const error = () => {
            console.log("Error Exporting All Rows");
        };

        axios.post(baseUrl, args, { responseType: 'blob' })
            .then((response) => {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                saveAs(blob, `exportedFile.${data.fileType}`);
                success();
            })
            .catch((err) => {
                error();
            });
    };
};

export const exportSelectedRows: ActionCreator<
  ThunkAction<any, any, any, any>
> = (data) => {
    return (dispatch: Dispatch<any>) => {
        let baseUrl = "";

        const args = {
            dataType: data.dataType,
            fileType: data.fileType,
            sortColumn: data.sortColumn,
            sortDirection: data.sortDirection,
            filters: null,
            typeID: null,
            fullPath: null,
            collaborate: data.collaborate,
            rowIDs: "",
            userId: null,
            developerId: null, // For Target Market, we need to send developerId, not userId.
        };

        switch (data.dataType) {
            case "Locations":
                baseUrl = "/DataForge/Stores/Export";
                args.typeID = data.type.id;

                for (let i = 0; i < data.selectedRows.length; i++) {
                    if (i === data.selectedRows.length - 1) {
                        args.rowIDs += data.selectedRows[i].id;
                    } else {
                        args.rowIDs += data.selectedRows[i].id + ",";
                    }
                }
                break;
            case "Pushpins":
                baseUrl = "/DataForge/Pushpins/Export";
                args.typeID = data.type.typeID;
                args.collaborate = !!data.collaborate;

                for (let i = 0; i < data.selectedRows.length; i++) {
                    if (i === data.selectedRows.length - 1) {
                        args.rowIDs += data.selectedRows[i].id;
                    } else {
                        args.rowIDs += data.selectedRows[i].id + ",";
                    }
                }
                break;
            case "Territories":
                baseUrl = "/DataForge/Territories/Export";
                args.typeID = data.type.typeID;
                args.collaborate = !!data.collaborate;

                for (let i = 0; i < data.selectedRows.length; i++) {
                    if (i === data.selectedRows.length - 1) {
                        args.rowIDs += data.selectedRows[i].id;
                    } else {
                        args.rowIDs += data.selectedRows[i].id + ",";
                    }
                }
                break;
            case "Areas of Interest/Points":
                baseUrl = "/DataForge/AreasOfInterest/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Areas of Interest/Geographies":
                baseUrl = "/DataForge/AreasOfInterest/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Results/Points":
                baseUrl = "/DataForge/Results/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Results/Geographies":
                baseUrl = "/DataForge/Results/Export";
                args.fullPath = data.type.fullPath;
                break;
            case "Target Markets":
                baseUrl = "/DataForge/TargetMarket/Export";
                args.typeID = data.type.typeId;
                args.developerId = data.type.developerId;

                // If no sort has been set, seed with default values.
                if (!args.sortColumn && !args.sortDirection) {
                    args.sortColumn = "locationName";
                    args.sortDirection = "ASC";
                }

                for (let i = 0; i < data.selectedRows.length; i++) {
                    if (i === data.selectedRows.length - 1) {
                        args.rowIDs += data.selectedRows[i].locationId;
                    } else {
                        args.rowIDs += data.selectedRows[i].locationId + ",";
                    }
                }
                break;
            default:
                return;
        }

        const success = () => {
            console.log("Successfully Exported Selected Rows");
        };

        const error = () => {
            console.log("Error Exporting Selected Rows");
        };

        axios.post(baseUrl, args, { responseType: 'blob' })
            .then((response) => {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                saveAs(blob, `exportedFile.${data.fileType}`);
                success();
            })
            .catch((err) => {
                error();
            });
    };
};
